import { useEffect, useMemo } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { listAssetsQueryKey } from '@/hooks/v1/hooks/useListAssets'
import type { Asset } from '@/hooks/v1/types/Asset'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

type UseAssetsSubscriptionOptions = {
  enabled?: boolean
}

export const useAssetsSubscription = ({ enabled = true }: UseAssetsSubscriptionOptions = {}) => {
  const { data: userSelfData } = useGetUserSelf()
  const userId = useMemo(() => userSelfData?.data?.id ?? null, [userSelfData])

  useEffect(() => {
    // Only subscribe if user is authenticated and has a uid
    if (!enabled || !userId) {
      return
    }

    const unsubscribe = subscribeRealtime(
      {
        onMessage: (data) => {
          try {
            if (isWebSocketErrorMessage(data)) {
              Logger.error('DEBUG WS ASSETS: Received error message, skipping setQueryData:', data)
              return
            }

            const updates: Asset[] = Array.isArray(data)
              ? data
              : data && typeof data === 'object'
                ? Object.values(data)
                : []

            if (!updates.length) {
              return
            }

            const queryKey = listAssetsQueryKey()
            queryClient.setQueryData(queryKey, (previous: any) => {
              const currentData: Asset[] = Array.isArray(previous?.data) ? previous.data : []
              const merged = new Map<string, Asset>()
              const withoutId: Asset[] = []

              const addAsset = (asset?: Asset) => {
                if (!asset) return
                const assetId = asset.id
                if (assetId) {
                  merged.set(assetId, asset)
                } else {
                  withoutId.push(asset)
                }
              }

              currentData.forEach(addAsset)
              updates.forEach(addAsset)

              return {
                ...(previous ?? {}),
                data: [...merged.values(), ...withoutId],
              }
            })
          } catch (_error) {
            // Logger.error('DEBUG WS ASSETS: Error parsing WebSocket message:', error)
          }
        },
        onOpen: () => {
          Logger.log('DEBUG WS ASSETS: WebSocket connected successfully to assets stream')
        },
        onError: (error) => {
          Logger.error('DEBUG WS ASSETS: WebSocket error:', error)
        },
        onClose: (event) => {
          Logger.log(
            'DEBUG WS ASSETS: WebSocket connection closed, code:',
            event.code,
            'wasClean:',
            event.wasClean,
          )
        },
      },
      {
        key: 'assets:all',
        path: '/assets',
        subscribeData: { subscribe: true },
        unsubscribeData: { subscribe: false },
        mapNotification: (data) =>
          data && typeof data === 'object' && 'assets' in data
            ? (data as { assets: unknown }).assets
            : data,
      },
    )

    return () => {
      unsubscribe()
    }
  }, [enabled, userId])
}
